import React, { useState, useEffect } from 'react';
import { Search, Plus, Pencil, Power, X, GitBranch, AlertCircle, Trash2, CheckCircle } from 'lucide-react';

const f = "'Inter',ui-sans-serif,system-ui,sans-serif";

const EMPTY_FORM = { category_id: '', department_id: '', status: 'active' };

function AlertModal({ title, message, onClose }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.4)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#FFFFFF', borderRadius: 16, width: 420, padding: '24px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', fontFamily: f, textAlign: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 48, height: 48, borderRadius: 12, background: '#FEE2E2', margin: '0 auto 16px' }}>
          <AlertCircle size={24} color="#DC2626" />
        </div>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1E293B', margin: '0 0 8px' }}>{title}</h3>
        <p style={{ fontSize: 13, color: '#64748B', margin: '0 0 20px', lineHeight: 1.5 }}>{message}</p>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <button onClick={onClose} style={{ height: 36, padding: '0 18px', borderRadius: 8, border: 'none', background: '#DC2626', color: '#FFFFFF', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: f }}>OK</button>
        </div>
      </div>
    </div>
  );
}

function ConfirmModal({ title, message, confirmText = 'Delete', confirmColor = '#DC2626', onConfirm, onCancel }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.4)', zIndex: 250, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#FFFFFF', borderRadius: 16, width: 420, padding: '24px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', fontFamily: f }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1E293B', margin: '0 0 8px' }}>{title}</h3>
        <p style={{ fontSize: 13, color: '#64748B', margin: '0 0 20px', lineHeight: 1.5 }}>{message}</p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onCancel} style={{ height: 36, padding: '0 18px', borderRadius: 8, border: '1px solid #E2E8F0', background: '#FFFFFF', fontSize: 13, fontWeight: 600, color: '#64748B', cursor: 'pointer', fontFamily: f }}>Cancel</button>
          <button onClick={onConfirm} style={{ height: 36, padding: '0 18px', borderRadius: 8, border: 'none', background: confirmColor, color: '#FFFFFF', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: f }}>{confirmText}</button>
        </div>
      </div>
    </div>
  );
}

function Modal({ onClose, onSave, initial = EMPTY_FORM, title = 'Add Mapping', categories = [], departments = [] }) {
  const [form, setForm] = useState(initial);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.4)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#FFFFFF', borderRadius: 16, width: 520, boxShadow: '0 20px 60px rgba(0,0,0,0.2)', fontFamily: f }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #F1F5F9', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1E293B', margin: '0 0 4px' }}>{title}</h2>
            <p style={{ fontSize: 13, color: '#64748B', margin: 0 }}>{title === 'Add Mapping' ? 'Map a ticket category to a business department.' : 'Update category-to-department mapping.'}</p>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, color: '#94A3B8' }}><X size={18} /></button>
        </div>
        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Ticket Category</label>
            <select value={form.category_id} onChange={e => set('category_id', e.target.value)} disabled={title !== 'Add Mapping'}
              style={{ width: '100%', height: 38, padding: '0 12px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 13, fontFamily: f, outline: 'none', boxSizing: 'border-box', color: '#1E293B', cursor: 'pointer' }}>
              <option value="">Select category</option>
              {categories.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Business Department</label>
            <select value={form.department_id} onChange={e => set('department_id', e.target.value)}
              style={{ width: '100%', height: 38, padding: '0 12px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 13, fontFamily: f, outline: 'none', boxSizing: 'border-box', color: '#1E293B', cursor: 'pointer' }}>
              <option value="">Select department</option>
              {departments.map(d => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Status</label>
            <select value={form.status} onChange={e => set('status', e.target.value)} style={{ width: '100%', height: 38, padding: '0 12px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 13, fontFamily: f, outline: 'none', boxSizing: 'border-box', color: '#1E293B', cursor: 'pointer' }}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
        <div style={{ padding: '14px 24px', borderTop: '1px solid #F1F5F9', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} style={{ height: 36, padding: '0 18px', borderRadius: 8, border: '1px solid #E2E8F0', background: '#FFFFFF', fontSize: 13, fontWeight: 600, color: '#64748B', cursor: 'pointer', fontFamily: f }}>Cancel</button>
          <button onClick={() => { if (form.category_id && form.department_id) { onSave(form); onClose(); } }} style={{ height: 36, padding: '0 18px', borderRadius: 8, border: 'none', background: '#1E293B', color: '#FFFFFF', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: f }}>{title === 'Add Mapping' ? 'Create Mapping' : 'Save Changes'}</button>
        </div>
      </div>
    </div>
  );
}

export default function AdminCategoryRouting() {
  const [mappings, setMappings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editMapping, setEditMapping] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [confirmModal, setConfirmModal] = useState(null);
  const [alertModal, setAlertModal] = useState(null);

  const getToken = () => sessionStorage.getItem('hcmsToken') || sessionStorage.getItem('token') || localStorage.getItem('hcmsToken') || localStorage.getItem('token');
  const getHeaders = () => ({ Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' });

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  const refresh = async () => {
    try {
      const [mappingsRes, categoriesRes, deptsRes] = await Promise.all([
        fetch('/api/admin/category-routing', { headers: { Authorization: `Bearer ${getToken()}` } }),
        fetch('/api/admin/categories', { headers: { Authorization: `Bearer ${getToken()}` } }),
        fetch('/api/admin/departments', { headers: { Authorization: `Bearer ${getToken()}` } })
      ]);

      if (!mappingsRes.ok) throw new Error('Failed to fetch mappings');
      if (!categoriesRes.ok) throw new Error('Failed to fetch categories');
      if (!deptsRes.ok) throw new Error('Failed to fetch departments');

      const mappingsData = await mappingsRes.json();
      const categoriesData = await categoriesRes.json();
      const deptsData = await deptsRes.json();

      setMappings(mappingsData.data || []);
      setCategories((categoriesData.data || []).filter(c => c.status === 'active'));
      setDepartments((deptsData.data || []).filter(d => d.status === 'active'));
    } catch (err) {
      console.error('Error loading category routing:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const handleCreate = async (form) => {
    try {
      const res = await fetch('/api/admin/category-routing', {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          category_id: parseInt(form.category_id),
          department_id: parseInt(form.department_id),
          status: form.status
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || data.message || 'Failed to create mapping');
      await refresh();
      showSuccess('Mapping created successfully');
    } catch (err) {
      console.error(err);
      setAlertModal({ title: 'Create Failed', message: err.message });
    }
  };

  const handleUpdate = async (form) => {
    if (!editMapping) return;
    try {
      const res = await fetch(`/api/admin/category-routing/${editMapping.id}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({
          department_id: parseInt(form.department_id),
          status: form.status
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || data.message || 'Failed to update mapping');
      await refresh();
      showSuccess('Mapping updated successfully');
    } catch (err) {
      console.error(err);
      setAlertModal({ title: 'Update Failed', message: err.message });
    }
  };

  const handleToggle = (m) => {
    setConfirmModal({
      title: m.status === 'active' ? 'Deactivate Mapping' : 'Activate Mapping',
      message: `Are you sure you want to ${m.status === 'active' ? 'deactivate' : 'activate'} the mapping for "${m.category_name}"?`,
      confirmText: m.status === 'active' ? 'Deactivate' : 'Activate',
      confirmColor: m.status === 'active' ? '#DC2626' : '#059669',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          const res = await fetch(`/api/admin/category-routing/${m.id}`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify({
              department_id: m.department_id,
              status: m.status === 'active' ? 'inactive' : 'active'
            })
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(data.error || data.message || 'Failed to toggle status');
          await refresh();
          showSuccess(m.status === 'active' ? 'Mapping deactivated successfully' : 'Mapping activated successfully');
        } catch (err) {
          console.error(err);
          setAlertModal({ title: 'Status Update Failed', message: err.message });
        }
      }
    });
  };

  const handleDelete = (m) => {
    setConfirmModal({
      title: 'Delete Mapping',
      message: `Delete the mapping for "${m.category_name}"? This action cannot be undone.`,
      confirmText: 'Delete',
      confirmColor: '#DC2626',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          const res = await fetch(`/api/admin/category-routing/${m.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${getToken()}` } });
          const data = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(data.error || data.message || 'Failed to delete mapping');
          await refresh();
          showSuccess('Mapping deleted successfully');
        } catch (err) {
          console.error(err);
          setAlertModal({ title: 'Delete Failed', message: err.message });
        }
      }
    });
  };

  const filtered = mappings.filter(m =>
    (m.category_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (m.department_name || '').toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div style={{ fontFamily: f, display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <div style={{ fontSize: 14, color: '#64748B' }}>Loading category routing...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ fontFamily: f, display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <div style={{ textAlign: 'center' }}>
          <AlertCircle size={48} color="#EF4444" style={{ marginBottom: 16 }} />
          <div style={{ fontSize: 14, color: '#EF4444', marginBottom: 8 }}>Error loading category routing</div>
          <div style={{ fontSize: 12, color: '#94A3B8' }}>{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: f }}>
      {successMsg && (
        <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 300, display: 'flex', alignItems: 'center', gap: 10, padding: '16px 24px', background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
          <CheckCircle size={20} color="#16A34A" />
          <span style={{ fontSize: 14, fontWeight: 600, color: '#16A34A' }}>{successMsg}</span>
        </div>
      )}
      {confirmModal && <ConfirmModal title={confirmModal.title} message={confirmModal.message} confirmText={confirmModal.confirmText} confirmColor={confirmModal.confirmColor} onConfirm={confirmModal.onConfirm} onCancel={() => setConfirmModal(null)} />}
      {alertModal && <AlertModal title={alertModal.title} message={alertModal.message} onClose={() => setAlertModal(null)} />}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1E293B', margin: '0 0 6px' }}>Category Routing</h1>
          <p style={{ fontSize: 13, color: '#64748B', margin: 0 }}>Map ticket categories to business departments for automatic routing</p>
        </div>
        <button onClick={() => { setEditMapping(null); setShowModal(true); }} style={{ display: 'flex', alignItems: 'center', gap: 6, height: 38, padding: '0 16px', borderRadius: 8, background: '#1E293B', color: '#FFFFFF', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: f }}>
          <Plus size={14} />Add Mapping
        </button>
      </div>

      <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 16, padding: '18px 20px', boxShadow: '0 1px 2px rgba(15,23,42,0.04)', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, maxWidth: 400 }}>
          <Search size={16} color="#94A3B8" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by category or department..."
            style={{ flex: 1, height: 36, border: 'none', outline: 'none', fontSize: 13, fontFamily: f, color: '#1E293B' }} />
        </div>
      </div>

      <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 16, boxShadow: '0 1px 2px rgba(15,23,42,0.04)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#F8FAFC', borderBottom: '1px solid #E2E8F0' }}>
              <th style={{ padding: '14px 20px', textAlign: 'left', fontWeight: 600, color: '#64748B', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Ticket Category</th>
              <th style={{ padding: '14px 20px', textAlign: 'left', fontWeight: 600, color: '#64748B', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Business Department</th>
              <th style={{ padding: '14px 20px', textAlign: 'left', fontWeight: 600, color: '#64748B', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</th>
              <th style={{ padding: '14px 20px', textAlign: 'right', fontWeight: 600, color: '#64748B', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={4} style={{ padding: 40, textAlign: 'center', color: '#94A3B8', fontSize: 13 }}>
                  {search ? 'No mappings match your search' : 'No mappings yet. Add your first mapping.'}
                </td>
              </tr>
            ) : (
              filtered.map(m => (
                <tr key={m.id} style={{ borderBottom: '1px solid #F1F5F9' }}>
                  <td style={{ padding: '14px 20px', color: '#1E293B', fontWeight: 500 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <GitBranch size={16} color="#94A3B8" />
                      {m.category_name}
                    </div>
                  </td>
                  <td style={{ padding: '14px 20px', color: '#64748B' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#3B82F6' }} />
                      {m.department_name}
                    </div>
                  </td>
                  <td style={{ padding: '14px 20px' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 999, background: m.status === 'active' ? '#D1FAE5' : '#FEE2E2', color: m.status === 'active' ? '#059669' : '#DC2626', fontSize: 12, fontWeight: 600 }}>
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: m.status === 'active' ? '#059669' : '#DC2626' }} />
                      {m.status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ padding: '14px 20px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6 }}>
                      <button onClick={() => { setEditMapping(m); setShowModal(true); }} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 6, color: '#94A3B8', borderRadius: 6 }}><Pencil size={15} /></button>
                      <button onClick={() => handleToggle(m)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 6, color: m.status === 'active' ? '#DC2626' : '#059669', borderRadius: 6 }}><Power size={15} /></button>
                      <button onClick={() => handleDelete(m)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 6, color: '#EF4444', borderRadius: 6 }}><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <Modal
          onClose={() => setShowModal(false)}
          onSave={editMapping ? handleUpdate : handleCreate}
          initial={editMapping ? {
            category_id: String(editMapping.category_id),
            department_id: String(editMapping.department_id),
            status: editMapping.status || 'active'
          } : EMPTY_FORM}
          title={editMapping ? 'Edit Mapping' : 'Add Mapping'}
          categories={categories}
          departments={departments}
        />
      )}
    </div>
  );
}
